import { memo, useState, useCallback } from 'react';
import { ChevronDown, Check, ArrowRight, Scissors, Timer, Link, Save, Edit3, Trash2, X } from 'lucide-react';
import { PLATFORMS, TYPES, STAGES } from '../../constants';
import { formatDate, isOverdue, generateId } from '../../utils/helpers';

export const PipelineCard = memo(({ item, user, move, upd, del }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState({});
  const [addingCutdown, setAddingCutdown] = useState(false);
  const [newCutdown, setNewCutdown] = useState({ title: '', platforms: [], status: 'pending' });

  const next = STAGES[STAGES.findIndex(s => s.id === item.status) + 1];
  const hasOverdue = isOverdue(item.deadlines?.edit) || isOverdue(item.deadlines?.publish);
  const cutdowns = item.cutdowns || [];
  const cutdownsDone = cutdowns.filter(cd => cd.status === 'done').length;
  const canAddCutdowns = ['editing', 'thumbnail', 'scheduled', 'published'].includes(item.status);

  const handleSave = useCallback(() => {
    upd(item.id, editData);
    setIsEdit(false);
  }, [item.id, editData, upd]);

  const handleStartEdit = useCallback(() => {
    setIsExpanded(true);
    setIsEdit(true);
    setEditData(item);
  }, [item]);

  const addCutdown = useCallback(() => {
    if (!newCutdown.title.trim()) return;
    const updatedCutdowns = [...cutdowns, { ...newCutdown, id: generateId() }];
    upd(item.id, { cutdowns: updatedCutdowns });
    setNewCutdown({ title: '', platforms: [], status: 'pending' });
    setAddingCutdown(false);
  }, [newCutdown, cutdowns, item.id, upd]);

  const updateCutdown = useCallback((cutdownId, updates) => {
    const updatedCutdowns = cutdowns.map(cd => cd.id === cutdownId ? { ...cd, ...updates } : cd);
    upd(item.id, { cutdowns: updatedCutdowns });
  }, [cutdowns, item.id, upd]);

  const deleteCutdown = useCallback((cutdownId) => {
    const updatedCutdowns = cutdowns.filter(cd => cd.id !== cutdownId);
    upd(item.id, { cutdowns: updatedCutdowns });
  }, [cutdowns, item.id, upd]);

  return (
    <div className={`bg-white rounded-xl border-2 ${hasOverdue ? 'border-red-300' : 'border-transparent'} shadow-sm overflow-hidden`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
            <p className="font-semibold text-slate-800 truncate">{item.title}</p>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {item.platforms?.map(p => {
                const pl = PLATFORMS.find(x => x.id === p);
                return pl && <pl.icon key={p} size={14} className={pl.color} />;
              })}
              {item.urgent && <span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full font-semibold">Urgent</span>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={handleStartEdit} className="p-2 bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200" title="Edit">
              <Edit3 className="w-4 h-4" />
            </button>
            {item.status === 'pending_approval' && user === 'zein' ? (
              <button onClick={() => move(item.id, 'approved')} className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200" title="Approve">
                <Check className="w-4 h-4" />
              </button>
            ) : next && (
              <button onClick={() => move(item.id, next.id)} className="p-2 bg-violet-100 text-violet-600 rounded-lg hover:bg-violet-200" title={`→ ${next.label}`}>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform cursor-pointer hover:text-slate-600 ${isExpanded ? 'rotate-180' : ''}`} onClick={() => setIsExpanded(!isExpanded)} />
          </div>
        </div>

        {/* Cutdowns indicator */}
        {cutdowns.length > 0 && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 text-orange-700 rounded-lg font-medium">
              <Scissors className="w-4 h-4" />
              {cutdowns.length} cutdown{cutdowns.length > 1 ? 's' : ''}
            </div>
            <span className={`px-2 py-1 rounded-lg font-medium ${cutdownsDone === cutdowns.length ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
              {cutdownsDone}/{cutdowns.length} done
            </span>
          </div>
        )}

        {item.deadlines?.edit && item.status === 'editing' && (
          <div className={`mt-3 text-sm flex items-center gap-1.5 ${isOverdue(item.deadlines.edit) ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
            <Timer className="w-4 h-4" /> Due {formatDate(item.deadlines.edit)} {isOverdue(item.deadlines.edit) && '⚠️'}
          </div>
        )}

        {isExpanded && (
          <CardExpanded
            item={item}
            isEdit={isEdit}
            editData={editData}
            setEditData={setEditData}
            handleSave={handleSave}
            setIsEdit={setIsEdit}
            handleStartEdit={handleStartEdit}
            move={move}
            del={del}
            user={user}
            next={next}
            canAddCutdowns={canAddCutdowns}
            cutdowns={cutdowns}
            addingCutdown={addingCutdown}
            setAddingCutdown={setAddingCutdown}
            newCutdown={newCutdown}
            setNewCutdown={setNewCutdown}
            addCutdown={addCutdown}
            updateCutdown={updateCutdown}
            deleteCutdown={deleteCutdown}
          />
        )}
      </div>
    </div>
  );
});

PipelineCard.displayName = 'PipelineCard';

const CardExpanded = memo(({
  item, isEdit, editData, setEditData, handleSave, setIsEdit, handleStartEdit,
  move, del, user, next, canAddCutdowns, cutdowns, addingCutdown, setAddingCutdown,
  newCutdown, setNewCutdown, addCutdown, updateCutdown, deleteCutdown
}) => (
  <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
    {isEdit ? (
      <EditForm editData={editData} setEditData={setEditData} handleSave={handleSave} setIsEdit={setIsEdit} />
    ) : (
      <ViewMode
        item={item}
        move={move}
        del={del}
        user={user}
        next={next}
        handleStartEdit={handleStartEdit}
        canAddCutdowns={canAddCutdowns}
        cutdowns={cutdowns}
        addingCutdown={addingCutdown}
        setAddingCutdown={setAddingCutdown}
        newCutdown={newCutdown}
        setNewCutdown={setNewCutdown}
        addCutdown={addCutdown}
        updateCutdown={updateCutdown}
        deleteCutdown={deleteCutdown}
      />
    )}
  </div>
));

CardExpanded.displayName = 'CardExpanded';

const EditForm = memo(({ editData, setEditData, handleSave, setIsEdit }) => (
  <>
    <input
      value={editData.title || ''}
      onChange={e => setEditData({ ...editData, title: e.target.value })}
      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:border-violet-400 focus:outline-none"
      placeholder="Title"
    />
    <div className="flex flex-wrap gap-2">
      {PLATFORMS.map(p => (
        <button
          key={p.id}
          onClick={() => setEditData({
            ...editData,
            platforms: (editData.platforms || []).includes(p.id)
              ? editData.platforms.filter(x => x !== p.id)
              : [...(editData.platforms || []), p.id]
          })}
          className={`text-xs px-3 py-1.5 rounded-lg font-semibold ${(editData.platforms || []).includes(p.id) ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}
        >
          {p.label}
        </button>
      ))}
    </div>
    <select
      value={editData.type || ''}
      onChange={e => setEditData({ ...editData, type: e.target.value })}
      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:border-violet-400 focus:outline-none"
    >
      <option value="">Content type...</option>
      {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
    </select>
    <input
      type="url"
      value={editData.links?.script || ''}
      onChange={e => setEditData({ ...editData, links: { ...editData.links, script: e.target.value } })}
      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
      placeholder="Script URL"
    />
    <input
      type="url"
      value={editData.links?.footage || ''}
      onChange={e => setEditData({ ...editData, links: { ...editData.links, footage: e.target.value } })}
      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
      placeholder="Footage URL"
    />
    <input
      type="date"
      value={editData.deadlines?.publish || ''}
      onChange={e => setEditData({ ...editData, deadlines: { ...editData.deadlines, publish: e.target.value } })}
      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
    />
    <textarea
      value={editData.notes || ''}
      onChange={e => setEditData({ ...editData, notes: e.target.value })}
      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
      rows={2}
      placeholder="Notes..."
    />
    <div className="flex gap-2">
      <button onClick={handleSave} className="flex-1 px-3 py-2 bg-violet-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-violet-700">
        <Save className="w-4 h-4" />Save
      </button>
      <button onClick={() => setIsEdit(false)} className="flex-1 px-3 py-2 bg-slate-100 text-slate-600 font-semibold rounded-lg hover:bg-slate-200">
        Cancel
      </button>
    </div>
  </>
));

EditForm.displayName = 'EditForm';

const ViewMode = memo(({
  item, move, del, user, next, handleStartEdit, canAddCutdowns, cutdowns,
  addingCutdown, setAddingCutdown, newCutdown, setNewCutdown, addCutdown,
  updateCutdown, deleteCutdown
}) => (
  <>
    {item.type && <p className="text-sm text-slate-600"><span className="text-slate-400">Type:</span> {item.type}</p>}
    {(item.links?.script || item.links?.footage) && (
      <div className="flex gap-3">
        {item.links.script && <a href={item.links.script} target="_blank" rel="noopener noreferrer" className="text-sm text-violet-600 hover:underline flex items-center gap-1"><Link className="w-4 h-4" />Script</a>}
        {item.links.footage && <a href={item.links.footage} target="_blank" rel="noopener noreferrer" className="text-sm text-violet-600 hover:underline flex items-center gap-1"><Link className="w-4 h-4" />Footage</a>}
      </div>
    )}
    {item.deadlines?.publish && <p className="text-sm text-slate-600"><span className="text-slate-400">Publish:</span> {formatDate(item.deadlines.publish)}</p>}
    {item.notes && <p className="text-sm text-slate-500 line-clamp-2">{item.notes}</p>}

    {/* Cutdowns Section */}
    {(canAddCutdowns || cutdowns.length > 0) && (
      <CutdownsSection
        cutdowns={cutdowns}
        canAddCutdowns={canAddCutdowns}
        addingCutdown={addingCutdown}
        setAddingCutdown={setAddingCutdown}
        newCutdown={newCutdown}
        setNewCutdown={setNewCutdown}
        addCutdown={addCutdown}
        updateCutdown={updateCutdown}
        deleteCutdown={deleteCutdown}
      />
    )}

    <div className="pt-3 border-t border-slate-100 space-y-3">
      {item.status === 'pending_approval' && user === 'zein' && (
        <div className="flex gap-2">
          <button onClick={() => move(item.id, 'approved')} className="flex-1 px-3 py-2 bg-green-100 text-green-700 font-semibold rounded-lg flex items-center justify-center gap-1 hover:bg-green-200">
            <Check className="w-4 h-4" />Approve
          </button>
          <button onClick={() => move(item.id, 'idea')} className="flex-1 px-3 py-2 bg-red-100 text-red-700 font-semibold rounded-lg flex items-center justify-center gap-1 hover:bg-red-200">
            <X className="w-4 h-4" />Reject
          </button>
        </div>
      )}
      <div>
        <label className="text-xs text-slate-400 font-medium mb-1 block">Move to stage</label>
        <select
          value={item.status}
          onChange={e => move(item.id, e.target.value)}
          className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 focus:border-violet-400 focus:outline-none"
        >
          <option value="idea">Idea</option>
          {STAGES.map(s => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>
      <div className="flex gap-2">
        {next && <button onClick={() => move(item.id, next.id)} className="flex-1 px-3 py-2 bg-violet-100 text-violet-700 font-semibold rounded-lg flex items-center justify-center gap-1 hover:bg-violet-200">
          <ArrowRight className="w-4 h-4" />{next.label}
        </button>}
        <button onClick={handleStartEdit} className="p-2 bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200">
          <Edit3 className="w-4 h-4" />
        </button>
        <button onClick={() => del(item.id)} className="p-2 bg-red-50 text-red-500 rounded-lg hover:bg-red-100">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  </>
));

ViewMode.displayName = 'ViewMode';

const CutdownsSection = memo(({
  cutdowns, canAddCutdowns, addingCutdown, setAddingCutdown, newCutdown,
  setNewCutdown, addCutdown, updateCutdown, deleteCutdown
}) => (
  <div className="pt-3 border-t border-slate-100">
    <div className="flex items-center justify-between mb-3">
      <h4 className="font-semibold text-slate-700 flex items-center gap-1.5">
        <Scissors className="w-4 h-4 text-orange-500" /> Cutdowns
      </h4>
      {canAddCutdowns && (
        <button
          onClick={() => setAddingCutdown(!addingCutdown)}
          className="text-xs px-2.5 py-1 bg-orange-100 text-orange-700 rounded-lg font-semibold hover:bg-orange-200"
        >
          + Add
        </button>
      )}
    </div>

    {addingCutdown && (
      <div className="mb-3 p-3 bg-orange-50 rounded-lg space-y-2">
        <input
          value={newCutdown.title}
          onChange={e => setNewCutdown({ ...newCutdown, title: e.target.value })}
          placeholder="Cutdown title (e.g., 'Hook clip for TikTok')"
          className="w-full border-2 border-orange-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
        />
        <div className="flex flex-wrap gap-1.5">
          {PLATFORMS.map(p => (
            <button
              key={p.id}
              onClick={() => setNewCutdown({
                ...newCutdown,
                platforms: newCutdown.platforms.includes(p.id)
                  ? newCutdown.platforms.filter(x => x !== p.id)
                  : [...newCutdown.platforms, p.id]
              })}
              className={`text-xs px-2.5 py-1 rounded-lg font-semibold ${newCutdown.platforms.includes(p.id) ? p.bg + ' ' + p.color : 'bg-white text-slate-500'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={addCutdown} className="flex-1 px-3 py-1.5 bg-orange-500 text-white font-semibold rounded-lg text-sm hover:bg-orange-600">
            Add Cutdown
          </button>
          <button onClick={() => { setAddingCutdown(false); setNewCutdown({ title: '', platforms: [], status: 'pending' }); }} className="px-3 py-1.5 bg-white text-slate-600 font-semibold rounded-lg text-sm">
            Cancel
          </button>
        </div>
      </div>
    )}

    {cutdowns.length > 0 && (
      <div className="space-y-2">
        {cutdowns.map(cd => (
          <div key={cd.id} className={`p-3 rounded-lg ${cd.status === 'done' ? 'bg-green-50' : 'bg-slate-50'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <p className={`font-medium text-sm ${cd.status === 'done' ? 'text-green-700 line-through' : 'text-slate-700'}`}>{cd.title}</p>
                <div className="flex items-center gap-1.5 mt-1.5">
                  {cd.platforms?.map(p => {
                    const pl = PLATFORMS.find(x => x.id === p);
                    return pl && <span key={p} className={`${pl.bg} ${pl.color} text-xs px-1.5 py-0.5 rounded font-medium`}>{pl.label}</span>;
                  })}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <select
                  value={cd.status}
                  onChange={e => updateCutdown(cd.id, { status: e.target.value })}
                  className={`text-xs px-2 py-1 rounded-lg font-semibold border-0 ${
                    cd.status === 'done' ? 'bg-green-100 text-green-700' :
                    cd.status === 'editing' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-200 text-slate-600'
                  }`}
                >
                  <option value="pending">Pending</option>
                  <option value="editing">Editing</option>
                  <option value="done">Done</option>
                </select>
                <button onClick={() => deleteCutdown(cd.id)} className="p-1 text-slate-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}

    {cutdowns.length === 0 && !addingCutdown && canAddCutdowns && (
      <p className="text-sm text-slate-400 text-center py-2">No cutdowns yet</p>
    )}
  </div>
));

CutdownsSection.displayName = 'CutdownsSection';
